<script>
// Slim top loading bar for route transitions. Route components resolve
// before the view swaps (bare dynamic imports in @router/routes), so the
// old page stays visible while a section chunk downloads — this bar is the
// only signal that a navigation is in flight. It appears only when
// resolution takes longer than SHOW_DELAY, keeping warm navigations silent.
const SHOW_DELAY = 150;
// Aborted navigations (next(false)) fire neither afterEach nor onError, so
// force-hide if nothing settles within this window.
const SAFETY_HIDE = 10000;

export default {
  data() {
    return {
      visible: false,
      progress: 0,
      finishing: false,
    };
  },

  created() {
    this.showTimer = null;
    this.hideTimer = null;
    this.safetyTimer = null;
    this.unhook = [
      this.$router.beforeEach((to, from, next) => {
        this.start();
        next();
      }),
      this.$router.afterEach(() => this.finish()),
    ];
    // onError has no unhook in vue-router 3; harmless for this
    // never-destroyed root-level component.
    this.$router.onError(() => this.finish());
  },

  beforeDestroy() {
    this.unhook.forEach((fn) => fn());
    clearTimeout(this.showTimer);
    clearTimeout(this.hideTimer);
    clearTimeout(this.safetyTimer);
  },

  methods: {
    start() {
      clearTimeout(this.safetyTimer);
      this.safetyTimer = setTimeout(() => this.hide(), SAFETY_HIDE);

      // A guard redirect re-enters here mid-navigation; keep the bar as-is.
      if (this.showTimer || (this.visible && !this.finishing)) return;

      if (this.finishing) {
        clearTimeout(this.hideTimer);
        this.hide();
      }

      this.showTimer = setTimeout(() => {
        this.showTimer = null;
        this.visible = true;
        // Double rAF so the 0-width state paints before the trickle starts.
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            this.progress = 85;
          })
        );
      }, SHOW_DELAY);
    },

    finish() {
      clearTimeout(this.showTimer);
      this.showTimer = null;
      clearTimeout(this.safetyTimer);
      this.safetyTimer = null;

      if (!this.visible) return;
      this.finishing = true;
      this.progress = 100;
      this.hideTimer = setTimeout(() => this.hide(), 400);
    },

    hide() {
      this.visible = false;
      this.finishing = false;
      this.progress = 0;
    },
  },
};
</script>

<template>
  <div
    v-if="visible"
    class="router-progress-bar"
    :class="{ 'is-finishing': finishing }"
    :style="{ width: progress + '%' }"
  ></div>
</template>

<style lang="scss" scoped>
.router-progress-bar {
  position: fixed;
  top: 0;
  left: 0;
  height: 3px;
  z-index: 3000;
  background: $color_primary;
  border-radius: 0 2px 2px 0;
  pointer-events: none;
  // Fast start, asymptotic tail — reads as progress without faking numbers.
  transition: width 8s cubic-bezier(0.05, 0.65, 0.15, 1);

  &.is-finishing {
    opacity: 0;
    transition: width 0.2s ease-out, opacity 0.3s ease 0.15s;
  }
}
</style>
